// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldSizeVariant } from "@awc/MobiusOverrides/FormField";
import { sizeVariantValues } from "@awc/MobiusOverrides/FormField/formStyles";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import { color, ellipsis, getSystemColor } from "@awc/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { css } from "styled-components";

const PlaceholderSelector = `
        &::placeholder,
        &::-webkit-input-placeholder
`;

export const placeholderStyle = css`
    ${PlaceholderSelector} {
        font-size: ${fontConfig.fontSize.small}px;
        font-style: normal;
        letter-spacing: 0;
        line-height: ${fontConfig.lineHeight.singleLineSmall} !important;
        max-width: 100%;
        color: ${getSystemColor("common.ui4")};
        font-weight: ${fontConfig.weight.regular};
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        border: "rectangle",
        sizeVariant: "default",
        backgroundColor: "common.ui1",
        labelProps: {
            mergeCss: true,
            size: fontConfig.fontSize.small,
            transform: "capitalize",
            weight: fontConfig.weight.medium,
            lineHeight: fontConfig.lineHeight.singleLineSmall,
            color: "text",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
                display: flex;
                align-items: center;
                & .hint-wrapper {
                    margin-top: 1px;
                    display: flex;
                    flex-grow: 1;
                    justify-content: flex-end;
                }
            `,
        },
        errorProps: {
            mergeCss: true,
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.regular,
            color: "danger.main",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
            `,
        },
        hintProps: {
            color: "text",
            size: fontConfig.fontSize.regular,
            lineHeight: fontConfig.lineHeight.singleLineRegular,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: ${({ labelPosition }: any) => (labelPosition === "left" ? "auto" : "100%")};
                input,
                textarea,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${placeholderStyle}
                        &:disabled {
                            ${PlaceholderSelector} {
                                color: ${color("text.main")};
                                font-style: normal;
                            }
                        }
                        &:not(:disabled):not(:focus):hover {
                            border: 1px solid ${color("common.ui8")};
                        }
                    }
                }
                input[aria-autocomplete="list"] {
                    ${PlaceholderSelector} {
                        font-style: normal;
                    }
                }
                ${FormFieldClickable} {
                    &&:hover {
                        ${FormFieldIcon} {
                            color: ${color("common.ui10")};
                        }
                    }
                }
                input,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${ellipsis(1)}
                        text-overflow: ellipsis;
                    }
                }
                input,
                textarea {
                    resize: vertical;
                }

                &[data-size="large"] {
                    && input {
                        padding: 0 16px 0 16px;
                    }

                    && ${IconWrapper} {
                        padding: 10px;
                    }
                }

                .end-adornment {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    display: flex;
                    align-items: center;
                }
                .clickable-wrapper {
                    position: absolute;
                    top: 0;
                    right: 0;
                    display: flex;
                    align-items: center;
                    gap: ${({ _sizeVariant }: any) =>
                        sizeVariantValues[_sizeVariant as FormFieldSizeVariant].iconPadding - 2}px;
                    padding: ${({ _sizeVariant }: any) =>
                        `0 ${sizeVariantValues[_sizeVariant as FormFieldSizeVariant].iconPadding - 2}px`};
                    & > ${FormFieldClickable} {
                        position: relative;
                        padding: 0;
                        width: 20px;
                        & ${FormFieldIcon} {
                            padding: 0;
                            width: auto;
                            color: ${color("secondary.main")};
                        }
                        &:not(disabled):hover {
                            & ${FormFieldIcon} {
                                color: ${color("common.ui10")};
                            }
                        }
                    }
                    &.icon-wrapper {
                        right: unset;
                        left: 0;
                    }
                }
            `,
            formField: css`
                ${({ labelPosition }: any) => {
                    if (labelPosition === "left") {
                        return css`
                            align-items: center;
                        `;
                    }
                }}
            `,
        },
    },
} as xBaseTheme["formField"];
